'use client';

import {
  QueryClient,
  QueryClientProvider,
  DefaultOptions,
  QueryCache,
  MutationCache,
} from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import {
  AxiosError
} from 'axios';
import { Env } from '@/libs/env';
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { useHandleQueryError } from '@/libs/handle-query-error';
import { persistOptions, localStoragePersister } from '@/libs/query-persistence';

const LazyReactQueryDevtools = dynamic(
  () =>
    import('@tanstack/react-query-devtools').then((mod) => mod.ReactQueryDevtools),
  {
    ssr: false,
  },
);

export const QueryProvider = ({ children }: { children: React.ReactNode; }) => {
  const handleError = useHandleQueryError();

  // 使用 ref 保存错误处理函数，避免重复创建
  const handleErrorRef = useRef(handleError);
  handleErrorRef.current = handleError;

  // 稳定的错误处理回调
  const stableHandleError = useCallback((error: unknown) => {
    handleErrorRef.current(error as Error);
  }, []);

  // 使用 useMemo 创建 QueryClient，确保只在依赖变化时重新创建
  const queryClient = useMemo(() => {
    const queryCache = new QueryCache({
      onError: stableHandleError,
    });
    const mutationCache = new MutationCache({
      onError: stableHandleError,
    });

    const defaultOptions: DefaultOptions<Error> = {
      queries: {
        // 重试策略：对于客户端错误（4xx）不重试，其他错误最多重试1次（减少重试次数）
        retry: (failureCount, error) => {
          const axiosError = error as AxiosError;
          // 4xx 客户端错误（如401未授权、404未找到等）不重试，直接失败
          if (axiosError.response?.status && axiosError.response.status >= 400 && axiosError.response.status < 500) {
            return false;
          }
          // 其他错误（网络错误、5xx服务器错误等）最多重试1次（原来2次）
          return failureCount < 1;
        },
        // 重试延迟：指数退避策略，最大延迟10秒（原来30秒）
        // 第1次重试：1秒，第2次重试：2秒...最大10秒
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
        // 数据新鲜时间：1分钟内数据被认为是新鲜的，不会自动重新获取（优化为1分钟）
        staleTime: 0,
        // 垃圾回收时间：数据在内存中保留5分钟，超时后被垃圾回收（原来10分钟）
        gcTime: 5 * 60 * 1000,
        // 窗口获得焦点时不自动重新获取数据（避免频繁请求）
        refetchOnWindowFocus: false,
        // 页面挂载时，如果数据已过期才重新获取
        refetchOnMount: true,
        // 网络重连时不自动重新获取数据（减少不必要的请求）
        refetchOnReconnect: false,
        // 网络模式：只在在线状态下执行查询
        networkMode: 'online',
      },
      mutations: {
        // Mutation重试策略：比查询更保守，因为写操作通常不是幂等的
        retry: (failureCount, error) => {
          const axiosError = error as AxiosError;

          // 只有在网络错误或连接超时时才重试一次
          // POST/PUT/DELETE 等写操作可能会产生副作用，所以重试次数要少
          if (axiosError.code === 'ERR_NETWORK' || axiosError.code === 'ECONNABORTED') {
            return failureCount < 1;
          }

          // 其他错误（如业务错误、服务器错误等）不重试，避免重复提交
          return false;
        },

        // Mutation重试延迟：固定1秒延迟，避免过于复杂的退避策略
        retryDelay: 1000,
      },
    };

    return new QueryClient({
      queryCache,
      mutationCache,
      defaultOptions,
    });
  }, [stableHandleError]);

  // 优化：只在客户端渲染时使用持久化功能
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const persister = useMemo(() => {
    // 只在客户端环境下创建持久化器
    if (!isClient) return null;
    return localStoragePersister;
  }, [isClient]);

  const memoPersistOptions = useMemo(() => {
    if (!isClient || !persister) return null;

    return {
      ...persistOptions,
      persister,
      dehydrateOptions: {
        ...((persistOptions as unknown as { dehydrateOptions?: unknown; }).dehydrateOptions || {}),
        shouldDehydrateQuery: (query: unknown) => {
          // 仅持久化显式标记了 meta.persist 的查询
          const q = query as { meta?: Record<string, unknown>; state?: { dataUpdatedAt?: number; }; };
          return q?.meta?.persist === true;
        },
      },
    };
  }, [persister, isClient]);

  // 在服务端渲染或客户端未准备好时，使用普通的 QueryClientProvider
  if (!isClient || !memoPersistOptions) {
    return (
      <QueryClientProvider client={queryClient}>
        {children}
        {Env.NODE_ENV === 'development' && Env.NEXT_PUBLIC_REACT_QUERY_DEVTOOLS === 'true' && (
          <LazyReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
    );
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={memoPersistOptions}
    >
      {children}
      {Env.NODE_ENV === 'development' && Env.NEXT_PUBLIC_REACT_QUERY_DEVTOOLS === 'true' && (
        <LazyReactQueryDevtools initialIsOpen={false} />
      )}
    </PersistQueryClientProvider>
  );
};